import { NewItemPrefix } from '@constants/entities/ui';
import { DomainItem } from '@constants/forms/scope-settings';
import {
  DomainsPayload,
  IDomainPayload,
  ISensitive,
  ISubDomainPayload,
} from '@store/services/projects/types';
import { RenamePair } from '@store/services/questionnaire/types';
import { add } from '@utils/drawers';
import { withId } from '@utils/helpers';
import { ISensitiveElement } from '@views/ProjectSettings/components/ProjectDetailsSettings/constants/sensitive-form';
import {
  mapRenamePairsForUpdate,
  normalizeAnswersToStrings,
} from '@views/ProjectSettings/components/ProjectDetailsSettings/helpers/renamePairs';

export const settingsRowToString = (row: string | RenamePair): string =>
  typeof row === 'string' ? row : row.new_name ?? row.old_name ?? '';

const rowIsNew = (id?: string) => !!id && id.includes(NewItemPrefix);
export const mapStringRowsForSave = (
  rows: { value: string; id?: string }[],
  previous: (string | RenamePair)[] | undefined,
  rowRenames: RenamePair[] = [],
): RenamePair[] => {
  const current = rows.map((r) => r.value.trim());
  const prevNormalized = normalizeAnswersToStrings(previous ?? []);
  return mapRenamePairsForUpdate(current, prevNormalized, rowRenames);
};

export const prepareDomains = (domains: DomainItem[]) =>
  domains.reduce((acc, { id, name, authority, subDomains }, num) => {
    if (!name.trim() || !authority.trim()) return acc;

    const domain: IDomainPayload = {
      ...add(!id.includes(NewItemPrefix), { id }),
      name,
      authority,
      num,
      children: subDomains.reduce((acc, { id, name, authority }, num) => {
        if (!name.trim() || !authority.trim()) return acc;

        const subDomain = {
          ...add(!id.includes(NewItemPrefix), { id }),
          name,
          authority,
          num,
        };

        return [...acc, subDomain];
      }, [] as ISubDomainPayload[]),
    };

    return [...acc, domain];
  }, [] as DomainsPayload);

const previousDetailStrings = (row?: ISensitive): string[] => {
  if (!row?.children?.length) return [];
  return normalizeAnswersToStrings(row.children as unknown[]);
};

const sensitiveNameToString = (name: string | RenamePair): string =>
  typeof name === 'string' ? name : name.new_name ?? name.old_name ?? '';

const getPreviousSensitiveItem = (
  previousSensitive: ISensitive[] | undefined,
  index: number,
  type: string,
) =>
  previousSensitive?.find(({ name }) => sensitiveNameToString(name) === type) ??
  previousSensitive?.[index];
const buildSensitiveNamePair = (
  el: ISensitiveElement,
  prevRow: ISensitive | undefined,
): RenamePair => {
  const currentType = el.type.trim();
  const isNew = rowIsNew((el as { id?: string }).id);
  if (isNew || !prevRow) {
    return { old_name: null, new_name: currentType };
  }
  const prevNameStr = sensitiveNameToString(prevRow.name);
  const typeRenames = el.typeRenames ?? [];
  const mapped =
    mapRenamePairsForUpdate([currentType], [prevNameStr], typeRenames)[0] ??
    ({ old_name: null, new_name: currentType } satisfies RenamePair);
  if (
    mapped.old_name === null &&
    mapped.new_name &&
    prevNameStr &&
    prevNameStr !== mapped.new_name
  ) {
    return { old_name: prevNameStr, new_name: mapped.new_name };
  }
  return mapped;
};
export const prepareSensitiveElements = (
  elements: ISensitiveElement[],
  previousSensitive?: ISensitive[],
): ISensitive[] =>
  elements.map((el, index) => {
    const prevRow = getPreviousSensitiveItem(previousSensitive, index, el.type);
    const children = mapRenamePairsForUpdate(
      el.details,
      previousDetailStrings(prevRow),
      el.detailsRenames ?? [],
    );
    return {
      name: buildSensitiveNamePair(el, prevRow),
      children,
    };
  });

export function parseStringArray(
  array: (string | RenamePair)[],
): { id: string; value: string }[] {
  return array.map((item) => {
    const value =
      typeof item === 'string' ? item : item.new_name ?? item.old_name ?? '';
    return withId({ value });
  });
}

export const isAnyFieldDirty = (
  dirtyFields: any,
  ignoreKeys: string[] = [],
) => {
  const checkDirty = (obj: any) => {
    for (const key in obj) {
      if (!ignoreKeys.includes(key) && Object.hasOwn(obj, key)) {
        if (obj[key] === true) {
          return true;
        }

        if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (checkDirty(obj[key])) {
            return true;
          }
        }
      }
    }

    return false;
  };

  return checkDirty(dirtyFields);
};
